import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { APIMajorVersions, TabNames } from "../Assets/variables";
import { parseApiProperties } from "../Utils/contentTransformers";
import { apisv1 } from "../Assets/API/apisv1";
import { apisv2 } from "../Assets/API/apisv2";
import MiniSearch from "minisearch";
import { Marker } from "react-mark.js";
import APICriteriaItem from "./APICriteriaItem";

const index = new MiniSearch({
    fields: ['name', 'parameterName', 'description', 'enumValues'],
    storeFields: ['name', 'parameterName', 'description', 'type', 'format', 'enumValues'],
    searchOptions: {
        combineWith: "AND",
        boost: {name: 3, parameterName: 2, description: 2, enumValues: 1},
        prefix: true,
        fuzzy: false
    }
});

const APICriteria = () => {
    const { setActiveTabItem, apiInfo } = useOutletContext();
    const searchInputElementRef = useRef('');
    const searchClearButtonElementRef = useRef(null);
    const resultListElementRef = useRef(null);
    let fieldsById = {};
    let fieldsSet = new Set();
    const [results, setResults] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [searchIndex, setSearchIndex] = useState(index);
    let allFieldsRef = useRef([]);
    let latestVersion = apiInfo.versions[apiInfo.versions.length - 1];
    
    const versionSelectorElementRef = useRef(latestVersion);
    const [selectedVersion, setSelectedVersion] = useState(latestVersion === APIMajorVersions.v1 ? apisv1 : apisv2);
    const propertiesRef = useRef(parseApiProperties(selectedVersion[apiInfo.apiName]));
    const [allFields, setAllFields] = useState(createAllFields());

    useEffect(() => {
        setActiveTabItem(TabNames.Criteria);
    }, []);

    useEffect(() => {
        selectMajorVersion();
        propertiesRef.current = parseApiProperties(selectedVersion[apiInfo.apiName]);
        allFieldsRef.current = createAllFields();
        setAllFields(allFieldsRef.current);
        index.removeAll();
        index.addAll(allFieldsRef.current);
        setSearchIndex(index);
        handleSearch()
    }, [selectedVersion]);

    const handleSearch = () => {
        const newResults = searchIndex?.search(searchInputElementRef.current.value, {});
        setInputValue(searchInputElementRef.current.value);
        setResults(newResults);

        if (searchInputElementRef.current.value.length > 0) {
            searchClearButtonElementRef.current.classList.add("tab-content__field-searchButton--shown");
            searchClearButtonElementRef.current.classList.remove("tab-content__field-searchButton--hidden");
        } else {
            searchClearButtonElementRef.current.classList.add("tab-content__field-searchButton--hidden");
            searchClearButtonElementRef.current.classList.remove("tab-content__field-searchButton--shown");
        }
    };

    // Clicking on clear button clears search
    const clearSearchField = () => {
        searchInputElementRef.current.value = '';
        searchInputElementRef.current.focus();
        searchClearButtonElementRef.current.classList.add("tab-content__field-searchButton--hidden");
        searchClearButtonElementRef.current.classList.remove("tab-content__field-searchButton--shown");
        setInputValue('');
        setResults(allFields);
    };

    function createAllFields() {
        let initAllFields = [];
        fieldsSet = new Set();
        Object.keys(propertiesRef.current).forEach((parameterField) => {
            let fieldKey = `${parameterField}`;
            let property = propertiesRef.current[parameterField];

            if (!fieldsSet.has(fieldKey) && property.description !== undefined) {

                let field = {
                    id: initAllFields.length,
                    name: parameterField,
                    parameterName: property.parameterName,
                    description: property.description ? property.description : '',
                    type: property.type ? property.type : '',
                    format: property.format ? property.format : '',
                    enumValues: property.enum ? property.enum : []
                };

                initAllFields.push(field);
                fieldsById[field.id] = field;
                fieldsSet.add(fieldKey);
            }
        });

        initAllFields.sort((a, b) => {
            const nameA = a.name.toUpperCase(); // ignore upper and lowercase
            const nameB = b.name.toUpperCase(); // ignore upper and lowercase
            if (nameA < nameB) {
                return -1;
            }
            if (nameA > nameB) {
                return 1;
            }

            // names must be equal
            return 0;
        });

        return initAllFields
    };

    function selectMajorVersion() {
        let selectedVersion = versionSelectorElementRef.current.value === APIMajorVersions.v2 ? apisv2 : apisv1;        
        setSelectedVersion(selectedVersion);
    }
    
    return (
        <div id="tab-content" className="api-panel">
            <header className="content-tab-header">
                <div className="content-tab-header__inner">
                    <div className="content-tab-header__searchbar">
                        <div className="searchbar-container">
                            <div id="search">
                                <input
                                    id="searchbar"
                                    type="text"
                                    name="search"
                                    placeholder="Search"
                                    autoComplete="none"
                                    autoCorrect="none"
                                    autoCapitalize="none"
                                    spellCheck="false"
                                    ref={searchInputElementRef}
                                    value={inputValue}
                                    onChange={() => handleSearch()} />
                                <button
                                    className="tab-content__field-searchButton tab-content__field-searchButton--hidden"
                                    id="clear"
                                    ref={searchClearButtonElementRef}
                                    onClick={() => clearSearchField()}>×</button>
                            </div>
                        </div>
                    </div>
                    <select name="version" id="selector-version" className="six-dropdown" ref={versionSelectorElementRef} onChange={() => selectMajorVersion()}>
                        { apiInfo.versions.filter((version) => version === APIMajorVersions.v2)[0] && 
                            <option value={APIMajorVersions.v2} defaultValue selected>
                                {`Version ${APIMajorVersions.v2}`}
                            </option> }
                        { apiInfo.versions.filter((version) => version === APIMajorVersions.v1)[0] &&
                            <option value={APIMajorVersions.v1}>
                                {`Version ${APIMajorVersions.v1}`}
                            </option> }
                    </select>
                </div>
            </header>
            <div id="result-list" ref={resultListElementRef}>
                <Marker mark={searchInputElementRef.current.value}>
                    {searchInputElementRef.current.value
                        ? results.map((result, index) => <APICriteriaItem result={result} key={index} />)
                        : allFields.map((result, index) => (
                            <APICriteriaItem result={result} key={index} />
                    ))}
                </Marker>
            </div>
        </div>
    );
};

export default APICriteria;